import { Fetcher } from '@this/src/util';
import type { ChatMessage, Direction, MessageType, RawSummary, Summary } from '../types/messages';

type ChatMessageResponse = {
  id: string;
  trip_id: number;
  direction: Direction;
  text: string;
  created_at: string;
  type?: MessageType;
  bundle_ids?: number[];
  item_ids?: number[];
  summary?: RawSummary;
  editable?: boolean;
  input_disabled?: boolean;
  proposal_change_disabled?: boolean;
};

type FetchAiMessagesResponse = {
  messages: ChatMessageResponse[];
};

type SendAiMessageResponse = {
  user_message: ChatMessageResponse;
};

export type RecentMessagePayload = {
  direction: Direction;
  text: string;
};

type CreateDraftResponse = {
  trip_id: number;
};

const toSummary = (raw: RawSummary): Summary => ({
  items: raw.items.map(item => ({
    category: item.category,
    label: item.label,
    text: item.text,
    price: item.price,
    priceWithTax: item.price_with_tax
  })),
  totalPrice: raw.total_price,
  totalPriceWithTax: raw.total_price_with_tax,
  hasSelection: raw.has_selection
});

const toChatMessage = (message: ChatMessageResponse): ChatMessage => ({
  id: message.id,
  direction: message.direction,
  text: message.text,
  createdAt: message.created_at,
  type: message.type,
  proposalBundleIds: message.bundle_ids,
  proposalItemIds: message.item_ids,
  summary: message.summary ? toSummary(message.summary) : undefined,
  editable: message.editable,
  inputDisabled: message.input_disabled,
  proposalChangeDisabled: message.proposal_change_disabled
});

export const fetchAiMessages = async (tripId: string) => {
  const response = await Fetcher.get<FetchAiMessagesResponse>(`/trips/${tripId}/ai_messages.json`);
  return response.messages.map(toChatMessage);
};

export const sendAiMessage = async (tripId: string, text: string, recentMessages: RecentMessagePayload[]) => {
  const response = await Fetcher.post<SendAiMessageResponse>(`/trips/${tripId}/ai_messages`, {
    ai_message: { text, recent_messages: recentMessages }
  });

  return toChatMessage(response.user_message);
};

export const createDraftTripWithMessage = async (text: string, recentMessages: RecentMessagePayload[]) => {
  const response = await Fetcher.post<CreateDraftResponse>('/trips/ai_messages', {
    ai_message: { text, recent_messages: recentMessages }
  });

  return {
    tripId: response.trip_id.toString()
  };
};
